import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './AdminFirmDetail.module.css';
import { firmApi } from '../../services/firmApi';
import { FirmDetailed, Country } from '../../types/firm';

const AdminFirmDetail: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const isEdit = !!firmId && firmId !== 'new';

  const [formData, setFormData] = useState<Partial<FirmDetailed>>({
    name: '',
    abbrev: '',
    url: '',
    domain: '',
    Country_ID: null,
    logo: '',
    primary_color: '',
    secondary_color: '',
    status: 1
  });
  const [countries, setCountries] = useState<Country[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [countrySearch, setCountrySearch] = useState<string>('');
  const [showCountryDropdown, setShowCountryDropdown] = useState<boolean>(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCountries();
    if (isEdit && firmId) {
      fetchFirmData();
    }
  }, [isEdit, firmId]);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter countries based on search
  const filteredCountries = countries.filter(country => 
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    (country.abbrev?.toLowerCase() || '').includes(countrySearch.toLowerCase())
  );

  // Get selected country name for display
  const getSelectedCountryName = () => {
    if (!formData.Country_ID) return '';
    const country = countries.find(c => String(c.Country_ID) === String(formData.Country_ID));
    if (!country) return '';
    return `${country.name}${country.abbrev ? ` [${country.abbrev}]` : ''}`;
  };

  const fetchCountries = async () => {
    try {
      const countriesData = await firmApi.getCountriesLookupForFirms();
      const normalizedCountries = countriesData.map((country: Country) => ({
        ...country,
        Country_ID: String(country.Country_ID)
      }));
      setCountries(normalizedCountries);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load countries data';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    }
  };

  const fetchFirmData = async () => {
    if (!firmId) return;

    try {
      setLoading(true);
      const firmData = await firmApi.getFirmById(firmId);

      const normalizedFirmData: Partial<FirmDetailed> = {
        ...firmData,
        Country_ID: firmData.Country_ID !== null && firmData.Country_ID !== undefined 
          ? String(firmData.Country_ID) 
          : null,
        url: firmData.url || '',
        domain: firmData.domain || '',
        primary_color: firmData.primary_color || '',
        secondary_color: firmData.secondary_color || '',
        logo: firmData.logo || ''
      };

      setFormData(normalizedFirmData);

      if (normalizedFirmData.logo) {
        setImagePreview(`data:image/png;base64,${normalizedFirmData.logo}`);
      }
    } catch (error: any) {
      console.error('Error fetching firm:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to load firm data';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'Country_ID') {
      setFormData(prev => ({
        ...prev,
        Country_ID: value === '' ? null : value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit', {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Only JPG and PNG files are allowed', {
          position: "top-right",
          autoClose: 3000
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        const base64Data = base64String.split(',')[1];
        setImagePreview(base64String);
        setFormData(prev => ({
          ...prev,
          logo: base64Data
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
    // Reset file input
    const fileInput = document.getElementById('logoInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Firm name is required';
    }

    if (!formData.abbrev?.trim()) {
      newErrors.abbrev = 'Firm abbreviation is required';
    }

    if (!formData.url?.trim()) {
      newErrors.url = 'Firm URL is required';
    }

    if (!formData.domain?.trim()) {
      newErrors.domain = 'Mail domain is required';
    }

    if (!formData.Country_ID) {
      newErrors.Country_ID = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting.', {
        position: "top-right",
        autoClose: 3000
      });
      return;
    }

    try {
      setLoading(true);

      const firmUpdateData = {
        name: formData.name?.trim() || '',
        abbrev: formData.abbrev?.trim() || '',
        Country_ID: formData.Country_ID || null,
        logo: formData.logo || null,
        url: formData.url?.trim() || null,
        primary_color: formData.primary_color || null,
        secondary_color: formData.secondary_color || null,
        domain: formData.domain?.trim() || null,
        status: formData.status || 1
      };

      if (isEdit && firmId) {
        await firmApi.updateFirm(firmId, firmUpdateData);
        toast.success('Firm updated successfully', {
          position: "top-right",
          autoClose: 3000
        });
      } else {
        const result = await firmApi.createFirm(firmUpdateData);
        toast.success('Firm created successfully', {
          position: "top-right",
          autoClose: 3000
        });
        if (result.Firm_ID) {
          navigate(`/admin/firms/${result.Firm_ID}`);
        }
      }
    } catch (error: any) {
      console.error('Error saving firm:', error);
      const errorMessage = error?.response?.data?.detail || `Failed to ${isEdit ? 'update' : 'create'} firm`;
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!firmId) return;

    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this firm?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await firmApi.deleteFirm(firmId);
                toast.success('Firm deleted successfully', {
                  position: "top-right",
                  autoClose: 3000
                });
                navigate('/admin/firms');
              } catch (error: any) {
                console.error('Error deleting firm:', error);
                const errorMessage = error?.response?.data?.detail || 'Failed to delete firm';
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 3000
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
      }
    );
  };

  if (loading && isEdit) {
    return (
      <div className={`container mt-5 ${styles.loadingContainer}`}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading firm data...</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/admin/firms"><i className="bi bi-building me-1"></i>Firms</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {isEdit ? formData.name || 'Edit Firm' : 'Create Firm'}
            </li>
          </ol>
        </nav>

        <div className="mb-4">
          <h2 className="mb-1">{isEdit ? 'Edit Firm' : 'Create Firm'}</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-building me-2"></i>
            {isEdit ? `Managing ${formData.name || 'firm'}` : 'Add a new firm to the system'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <div className={styles.formCard}>
                <div className={styles.formCardHeader}>
                  <h5 className="mb-0">
                    <i className="bi bi-image me-2"></i>
                    Firm Logo
                  </h5>
                </div>
                <div className={`${styles.formCardBody} text-center`}>
                  <div className={styles.logoContainer}>
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Firm Logo"
                        className={styles.logoPreview}
                      />
                    ) : (
                      <div className={styles.logoPlaceholder}>
                        <i className="bi bi-building display-3 text-muted"></i>
                      </div>
                    )}
                  </div>
                  <p className="text-muted small mb-3">
                    JPG or PNG no larger than 5 MB
                  </p>
                  <input
                    type="file"
                    id="logoInput"
                    accept="image/png, image/jpeg"
                    className="d-none"
                    onChange={handleFileChange}
                  />
                  <button 
                    type="button"
                    className="btn btn-outline-primary w-100" 
                    onClick={() => document.getElementById('logoInput')?.click()}
                  >
                    <i className="bi bi-upload me-2"></i>
                    Upload Logo
                  </button>
                  {imagePreview && (
                    <button 
                      type="button"
                      className="btn btn-outline-danger w-100 mt-2" 
                      onClick={handleRemoveLogo}
                    >
                      <i className="bi bi-trash me-2"></i>
                      Remove Logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-8">
              <div className={styles.formCard}>
                <div className={styles.formCardHeader}>
                  <h5 className="mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Firm Details
                  </h5>
                </div>
                <div className={styles.formCardBody}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="name">
                        Firm Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter firm name" 
                        value={formData.name || ''}
                        onChange={handleInputChange}
                        required 
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="abbrev">
                        Abbreviation <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${errors.abbrev ? 'is-invalid' : ''}`}
                        id="abbrev"
                        name="abbrev"
                        type="text" 
                        placeholder="Enter abbreviation"
                        value={formData.abbrev || ''}
                        onChange={handleInputChange}
                        required 
                      />
                      {errors.abbrev && <div className="invalid-feedback">{errors.abbrev}</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="url">
                        Website URL <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${errors.url ? 'is-invalid' : ''}`}
                        id="url"
                        name="url"
                        type="url"
                        placeholder="https://example.com" 
                        value={formData.url || ''}
                        onChange={handleInputChange}
                        required 
                      />
                      {errors.url && <div className="invalid-feedback">{errors.url}</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="domain">
                        Mail Domain <span className="text-danger">*</span>
                      </label>
                      <input 
                        className={`form-control ${errors.domain ? 'is-invalid' : ''}`}
                        id="domain"
                        name="domain"
                        type="text"
                        placeholder="example.com (without @)" 
                        value={formData.domain || ''}
                        onChange={handleInputChange}
                        required 
                      />
                      {errors.domain && <div className="invalid-feedback">{errors.domain}</div>}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="Country_ID">
                        Country <span className="text-danger">*</span>
                      </label>
                      <div ref={countryDropdownRef} style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={`form-control ${errors.Country_ID ? 'is-invalid' : ''}`}
                          placeholder="Search countries..."
                          value={formData.Country_ID ? getSelectedCountryName() : countrySearch}
                          onChange={(e) => {
                            setCountrySearch(e.target.value);
                            setShowCountryDropdown(true);
                            if (!e.target.value) {
                              setFormData(prev => ({ ...prev, Country_ID: null }));
                            }
                          }}
                          onFocus={() => setShowCountryDropdown(true)}
                        />
                        {showCountryDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.375rem',
                            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            marginTop: '0.25rem'
                          }}>
                            {filteredCountries.map(country => (
                              <div
                                key={country.Country_ID}
                                style={{
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, Country_ID: String(country.Country_ID) }));
                                  setCountrySearch('');
                                  setShowCountryDropdown(false);
                                }}
                              >
                                {country.name}{country.abbrev ? ` [${country.abbrev}]` : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        {errors.Country_ID && <div className="invalid-feedback d-block">{errors.Country_ID}</div>}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="primary_color">
                        Primary Color
                      </label>
                      <div className={styles.colorInputGroup}>
                        <input 
                          className="form-control"
                          id="primary_color"
                          name="primary_color"
                          type="color"
                          value={formData.primary_color || '#007bff'}
                          onChange={handleInputChange}
                        />
                        <input 
                          className="form-control"
                          type="text"
                          placeholder="#007bff"
                          value={formData.primary_color || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold" htmlFor="secondary_color">
                        Secondary Color
                      </label>
                      <div className={styles.colorInputGroup}>
                        <input 
                          className="form-control"
                          id="secondary_color"
                          name="secondary_color"
                          type="color"
                          value={formData.secondary_color || '#6c757d'}
                          onChange={handleInputChange}
                        />
                        <input 
                          className="form-control"
                          type="text"
                          placeholder="#6c757d"
                          value={formData.secondary_color || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, secondary_color: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-4 pt-3 border-top flex-wrap">
                    <button 
                      className="btn btn-primary px-4" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {isEdit ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          {isEdit ? 'Update Firm' : 'Create Firm'}
                        </>
                      )}
                    </button>

                    {isEdit && (
                      <button 
                        type="button"
                        className="btn btn-danger px-4"
                        onClick={handleDelete}
                      >
                        <i className="bi bi-trash-fill me-2"></i>
                        Delete Firm
                      </button>
                    )}

                    <Link to="/admin/firms" className="btn btn-outline-secondary px-4">
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Firms
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminFirmDetail;
